// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Torc — one round, decoupled from Trystero, the DOM and the clock (driven by submit() / receive() /
// tick()), which is what makes the host-transfer and walkover contracts testable without a network
// at all (tests/takeover.test.ts).
//
// LOCKSTEP, AND THE DECISION THAT REMOVES A WHOLE CLASS OF HANGS: every message carries the ENTIRE
// ply log, not the latest ply. A whole game is at most ~120 small integers, so the wire cost is
// nothing, and in exchange a dropped datagram becomes unobservable — the next message reconstructs
// the world. The classic lockstep failure ("peer A sent ply 11, it was lost on a turn boundary, both
// peers now wait for each other forever") cannot occur, because there is no delta to lose. A 2s
// heartbeat re-sends the same log, so even a peer that goes quiet self-heals within one beat, and a
// spectator who arrives at ply 30 is instantly in sync.
//
// A received log is REPLAYED through this peer's own genPlies(). A ply that is not legal here means
// the two sides are running different rules — a stale cached build — and that is reported as exactly
// that, rather than silently diverging.

import {
  RULES_REV,
  createState,
  digest,
  genPlies,
  isOver,
  longestRun,
  markerCount,
  step,
  type PlyEvent,
  type State,
  type WinReason,
} from './rules';
import { modeOf, type Mode } from './modes';
import { searchTurn, type SearchOpts } from './bot';

export interface WireMsg {
  /** Round number, so a message from the previous round cannot disturb this one. */
  r: number;
  /** The whole ply log. */
  p: number[];
  /** FNV digest of the state after replaying it. */
  d: number;
  /** Rules revision — a mismatch is a build mismatch, not a desync. */
  v: number;
}

export type DuelFault = null | 'desync' | 'build';

export interface ResultRow {
  side: number;
  name: string;
  isSelf: boolean;
  isBot: boolean;
  won: boolean;
  /** Lines claimed — the score. */
  claims: number;
  /** Ring moves made. */
  rides: number;
  /** Jumps taken, and studs turned over by them. */
  jumps: number;
  flips: number;
  /** The longest line this side ever took off the board. */
  bestClaim: number;
  /** Studs of this colour left on the field. */
  studs: number;
  /** The longest line still standing but unfinished — what they missed. */
  unfinished: number;
  left: boolean;
}

export interface DuelDeps {
  send: (m: WireMsg) => void;
  onChange: () => void;
  onEnd: () => void;
  onEvents: (events: PlyEvent[], s: State) => void;
}

export interface DuelOpts {
  mode: Mode;
  seed: number;
  round: number;
  names: [string, string];
  /** null = spectator. */
  mySide: number | null;
  /** Solo only: the bot plays the other side. */
  bot: SearchOpts | null;
  deps: DuelDeps;
  /** Per-ply clock in ms, 0 = none. Multiplayer only. */
  clockMs?: number;
}

/**
 * Peers alternate which side they take each round.
 *
 * This is the mechanism that actually keeps a MATCH fair, whatever a single opening is worth. The
 * simulation puts side bias inside a couple of points of even across the three modes, so this is
 * insurance rather than a correction — but it costs nothing and it means a run of rounds is fair
 * even if one mode's opening turns out not to be.
 */
export const sideForRound = (rosterIndex: number, round: number): number => (rosterIndex + round + 1) % 2;

/** Torc is a duel: the first two roster entries play, everyone after them watches. */
export const SEATS = 2;

/**
 * Who am I this round — a side, or `null` for a spectator?
 *
 * THE SLICE IS THE WHOLE POINT. `maxPlayers: 2` on the lobby is only a caption; the engine's rematch
 * freezes whatever roster voted, so a THIRD peer really does arrive with `seated: true`. Run their
 * index straight through sideForRound and it aliases onto side 0 — `(i + round + 1) % 2` maps index
 * 2 to the same side as index 0 — so two peers both believe they are Amber, both submit a ply for
 * it, both latch a desync, and the round dies for all three. Sharing an invite link into a group
 * chat is the ordinary way a room fills, so a third arrival is the expected case, not an exotic one.
 */
export function seatFor(ids: readonly string[], selfId: string, round: number, seatedFlag: boolean): number | null {
  const idx = ids.slice(0, SEATS).indexOf(selfId);
  if (!seatedFlag || idx < 0) return null;
  return sideForRound(idx, round);
}

const HOLD_MS = 12_000;

/**
 * How long the room may go silent before the survivor stops trusting `onPeerLeave`.
 *
 * Learned the hard way across this fleet: when the opposing tab goes away abruptly, Trystero may
 * never report the peer as gone, so the walkover never arms and the survivor sits waiting
 * indefinitely with a live board. A peer that is present sends a heartbeat every 2s, so twenty
 * seconds of nothing is not a slow player — it is an absent one, and this makes the walkover
 * reachable without depending on the transport noticing anything at all.
 */
const SILENCE_MS = 20_000;

export class Duel {
  readonly state: State;
  readonly mode: Mode;
  readonly names: [string, string];
  readonly mySide: number | null;
  readonly bot: SearchOpts | null;
  readonly clockMs: number;
  fault: DuelFault = null;
  /** Side that has gone quiet, and how long is left before the walkover lands. */
  missing: number | null = null;
  holdLeft = 0;
  turnLeft = 0;
  botThinking = false;
  silence = 0;
  private readonly networked: boolean;
  private ended = false;
  /** Public so the game screen can take over the callbacks when it mounts. */
  deps: DuelDeps;
  private readonly round: number;

  constructor(o: DuelOpts) {
    this.mode = o.mode;
    this.state = createState(o.mode, o.seed);
    this.names = o.names;
    this.mySide = o.mySide;
    this.bot = o.bot;
    this.deps = o.deps;
    this.round = o.round;
    this.clockMs = o.clockMs ?? 0;
    this.turnLeft = this.clockMs;
    this.networked = o.bot === null && o.mySide !== null;
  }

  get isMyTurn(): boolean {
    return this.mySide !== null && this.state.turn === this.mySide && !isOver(this.state) && this.fault === null;
  }

  /** True while the side to act is a local bot rather than a person. */
  get isBotTurn(): boolean {
    return this.bot !== null && this.mySide !== null && this.state.turn !== this.mySide && !isOver(this.state);
  }

  legal(): number[] {
    if (this.fault !== null || isOver(this.state)) return [];
    return genPlies(this.state);
  }

  /** Commit a ply chosen locally (by the player, the local bot, or the ply clock). */
  submit(ply: number): boolean {
    if (isOver(this.state) || this.fault !== null) return false;
    const events: PlyEvent[] = [];
    if (!step(this.state, ply >>> 0, events)) return false;
    this.turnLeft = this.clockMs;
    this.deps.onEvents(events, this.state);
    this.broadcast();
    this.deps.onChange();
    this.checkEnd();
    return true;
  }

  /** Re-send the whole log. Cheap, idempotent, and the reason a lost message is not a hang. */
  broadcast(): void {
    if (this.mySide === null && this.state.log.length === 0) return;
    this.deps.send({ r: this.round, p: this.state.log.slice(), d: digest(this.state), v: RULES_REV });
  }

  receive(msg: WireMsg): void {
    if (!msg || msg.r !== this.round || this.fault !== null) return;
    // Anything at all from the other side counts as "still there", including a heartbeat carrying a
    // log we already have.
    this.silence = 0;
    if (this.missing !== null) this.peerBack();
    if (msg.v !== RULES_REV) {
      this.fail('build');
      return;
    }
    const incoming = Array.isArray(msg.p) ? msg.p : [];
    if (incoming.length <= this.state.log.length) {
      // They are behind or level. If level and we disagree, one of us is not running these rules.
      if (incoming.length === this.state.log.length && typeof msg.d === 'number' && msg.d !== digest(this.state)) {
        this.fail('desync');
      }
      return;
    }
    for (let i = 0; i < this.state.log.length; i++) {
      if ((incoming[i] >>> 0) !== this.state.log[i]) {
        this.fail('desync');
        return;
      }
    }
    const events: PlyEvent[] = [];
    for (let i = this.state.log.length; i < incoming.length; i++) {
      if (isOver(this.state)) break;
      if (!step(this.state, incoming[i] >>> 0, events)) {
        this.fail('desync');
        return;
      }
    }
    if (typeof msg.d === 'number' && msg.d !== digest(this.state)) {
      this.fail('desync');
      return;
    }
    this.missing = null;
    this.holdLeft = 0;
    this.turnLeft = this.clockMs;
    this.deps.onEvents(events, this.state);
    this.deps.onChange();
    this.checkEnd();
  }

  /**
   * Advance the wall clock. Everything time-based lives here and is driven by setInterval from the
   * screen — never rAF, which a backgrounded tab throttles to nothing.
   */
  tick(dtMs: number): void {
    if (isOver(this.state) || this.fault !== null) return;

    if (this.networked && this.missing === null) {
      this.silence += dtMs;
      if (this.silence >= SILENCE_MS && this.mySide !== null) {
        this.peerLeft(1 - this.mySide);
        return;
      }
    }

    if (this.missing !== null) {
      this.holdLeft = Math.max(0, this.holdLeft - dtMs);
      if (this.holdLeft <= 0) {
        this.walkover();
        return;
      }
      this.deps.onChange();
      return;
    }

    if (this.clockMs > 0 && this.isMyTurn) {
      this.turnLeft = Math.max(0, this.turnLeft - dtMs);
      if (this.turnLeft <= 0) {
        // Never a forfeit and never a freeze: your own peer plays your best ply, so it comes from
        // the same place every other ply comes from and lockstep is untouched.
        const opts: SearchOpts = this.bot ?? { depth: 2, timeMs: 300, width: 6, placeWidth: 6 };
        const ply = searchTurn(this.state, opts).ply;
        if (ply >= 0) this.submit(ply);
        return;
      }
      this.deps.onChange();
    }
  }

  /** The other peer vanished. Hold for a moment — a phone changing network does this. */
  peerLeft(side: number): void {
    if (isOver(this.state) || this.mySide === null || side === this.mySide) return;
    this.missing = side;
    this.holdLeft = HOLD_MS;
    this.deps.onChange();
  }

  peerBack(): void {
    this.silence = 0;
    if (this.missing === null) return;
    this.missing = null;
    this.holdLeft = 0;
    this.broadcast();
    this.deps.onChange();
  }

  private walkover(): void {
    if (isOver(this.state) || this.mySide === null) return;
    // A room that collapses to one survivor is a WALKOVER — the survivor wins and reaches the full
    // results screen. But a round nobody played is NO CONTEST: putting a number on the match tally
    // for an empty field would be a lie about what happened.
    const played = this.state.log.length > 0;
    this.state.winner = played ? this.mySide : -1;
    this.state.reason = (played ? 'walkover' : 'nocontest') as WinReason;
    this.deps.onChange();
    this.checkEnd();
  }

  /**
   * A fault ENDS THE ROUND rather than only setting a flag and repainting, which would leave both
   * peers on a frozen board with a one-line explanation and no way forward — the exact dead-end
   * principle #12 forbids. There is no winner (the two boards disagree, so neither reading is
   * authoritative), which is what `nocontest` means.
   */
  private fail(kind: Exclude<DuelFault, null>): void {
    if (this.fault !== null) return;
    this.fault = kind;
    if (!isOver(this.state)) {
      this.state.winner = -1;
      this.state.reason = 'nocontest';
    }
    this.deps.onChange();
    this.checkEnd();
  }

  private checkEnd(): void {
    if (!isOver(this.state) || this.ended) return;
    this.ended = true;
    this.deps.onEnd();
  }

  get reason(): WinReason {
    return this.state.reason;
  }

  /** Every player's outcome, always — never just the local one. */
  results(): ResultRow[] {
    const s = this.state;
    return [0, 1].map((side) => {
      const st = s.stats[side];
      return {
        side,
        name: this.names[side],
        isSelf: this.mySide === side,
        isBot: this.bot !== null && this.mySide !== side,
        won: s.winner === side,
        claims: s.claimed[side],
        rides: st.turns,
        jumps: st.jumps,
        flips: st.flips,
        bestClaim: st.longest,
        studs: markerCount(s, side),
        // What they MISSED: the longest line of theirs still standing when it stopped. On a board
        // needing five, a four sitting there is the whole story of the last few turns.
        unfinished: longestRun(s, side),
        left: this.missing === side,
      };
    });
  }
}

export function duelNames(a: string, b: string): [string, string] {
  return [a || 'Amber', b || 'Teal'];
}

export const modeFromOpts = (opts: { mode?: string } | null | undefined): Mode => modeOf(opts?.mode);
